/* eslint-disable no-irregular-whitespace */
import Serializer, { type DeserializeArguments } from './Serializer';
import { type Descendant } from 'slate';
import { type HtmlSerializerContext } from '../../serialization/HtmlSerializer';
import { isHTMLElementNode, isTextNode } from '../helpers/serializerHelpers';

const collapseWhitespace = (text: string | null): string => {
  if (!text) return '';
  return text.replace(/\s+/g, ' ');
};

const isSpanWithSingleNoBreakSpace = (element: Node) =>
  isHTMLElementNode(element) &&
  element.tagName &&
  element.tagName.toLowerCase() === 'span' &&
  element.childNodes.length === 1 &&
  element.childNodes[0].nodeName === '#text' &&
  element.childNodes[0].nodeValue === ' '; // no-break space U+00a0

const preserveWhitespace = (context: HtmlSerializerContext) => {
  return (context.preDepth ?? 0) > 0 || context?.inGoogleSheetCell;
};

const skipEmpty = (context: any) => context.treeDepth === 1;

class SerializerText extends Serializer {
  static deserialize({
    el,
    markAttributes,
    context,
    options,
  }: DeserializeArguments) {
    if (isHTMLElementNode(el) && el.tagName.toLowerCase() === 'br') {
      return {
        text: '\n',
        ...markAttributes,
      } as Descendant;
    }

    // Convert spans with a single no-break space into a text node with a single space.
    // This fixes an issue that started happening with Chrome and Safari where copying and pasting text
    // from those browsers ended up removing spaces between some tags.
    // For example, `hello<span> </span><b>there</b>` would become `hello<b>there</b>` instead of
    // `hello <b>there</b>`
    if (isSpanWithSingleNoBreakSpace(el)) {
      return {
        text: ' ',
        ...markAttributes,
      } as Descendant;
    }

    if (isTextNode(el)) {
      if (el.nodeValue && el.nodeValue.match(/<!--.*?-->/)) return;
      const text =
        preserveWhitespace(context) || options.preserveWhitespace
          ? el.nodeValue
          : collapseWhitespace(el.nodeValue);

      if (text === ' ' && skipEmpty(context)) {
        return null;
      }

      return {
        text,
        ...markAttributes,
      } as Descendant;
    }
  }

  static serialize(): React.ReactNode {
    return null;
  }
}

export default SerializerText;
